import { SentryBreadcrumb } from '@common/sentry/SentryBreadcrumb';
import { Entity } from '@common/types/Entity';
import { EntityEvents } from '@common/user-analytics';
import { UserTypes } from '@common/UserTypes';
import { event as fullStoryEvent, identify, init as FullStoryInit, setUserVars } from '@fullstory/browser';
import { EventName, EventType, metrics } from '@prophecy/metrics';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { addBreadcrumb, setUser } from '@sentry/react';
import { nanoid } from 'nanoid';
import { useMemo, useRef } from 'react';
import { useImmer } from 'use-immer';

import { csrf } from '../../utils/csrf';
import { FULLSTORY_ENABLE_KEY } from '../../utils/localstorage-keys';
import { replayToken } from '../../utils/replayToken';
import { shareToken } from '../../utils/shareToken';
import { useAppMetadata } from '../appMetadata';
import { useGlobalVariables } from '../globalConfig/securedConfigHook';
import { MixPanelContext, MixPanelContextKeys, MixpanelStateType } from './types';
import {
  FullStoryAllowedDomains,
  fullStoryNameSpace,
  fullStoryOrgID,
  getCurrentTimeStamp,
  getMixpanelApIhost,
  getNameFromEmail,
  getSessionValue,
  MIXPANEL___TOKEN,
  MIXPANEL_ALLOWED_HOSTS
} from './util';

// mixpanel-browser is ~272 KB raw. Loading it statically pulls it into the eager `shell-context`
// chunk that every route (incl. the public sign-in/sign-up pages) downloads before first paint.
// Every call site below runs inside an async callback, so we lazy-load it once on first use and
// cache the promise — keeping analytics out of the critical path without changing behavior.
type MixpanelInstance = (typeof import('mixpanel-browser'))['default'];
let mixpanelPromise: Promise<MixpanelInstance | null> | null = null;
const loadMixpanel = () => {
  if (!mixpanelPromise) {
    mixpanelPromise = import('mixpanel-browser')
      .then((m) => m.default)
      .catch((error) => {
        // Don't cache the failure — clearing the ref lets a later call retry the import (transient
        // network, or a stale chunk hash after a deploy). Return null so callers degrade gracefully
        // (drop the event) instead of throwing an unhandled rejection from a fire-and-forget call.
        mixpanelPromise = null;
        console.error('Failed to load mixpanel-browser', error);
        return null;
      });
  }
  return mixpanelPromise;
};

export const useMixPanelContext = () => {
  const appData = useAppMetadata();
  //Current user data is active user data whose project we are opening like share or normal project (owner of the active project and not the owner of active session)
  const currentUserData = appData?.user;
  //Logged in user data is the user data who is actually logged in using his/her credentials (owner of the active session)
  //Case when user has opened the replay or share project and logged in with some credentials then fetch loggedin user data
  const loggedInUserData = appData?.loggedInUserData;
  const isMixPanelEnabled = useGlobalVariables().isMixPanelEnabled;
  const [state, setState] = useImmer<MixpanelStateType>({
    enabled: false,
    isUserIdentified: false,
    isMixPanelLoaded: false,
    contexts: {}
  });

  // Resolves once mixpanel has finished initializing. Event methods await this instead of dropping
  // events fired before init completes — so the app shell no longer has to block its first paint
  // until analytics is ready (mixpanel-browser is now a lazily-loaded chunk). Early `track` calls
  // simply queue on this promise and flush the moment init resolves; nothing is lost.
  const readyRef = useRef<{ promise: Promise<void>; resolve: () => void; reject: (error: Error) => void } | null>(null);
  if (!readyRef.current) {
    let resolve!: () => void;
    let reject!: (error: Error) => void;
    const promise = new Promise<void>((r, e) => {
      resolve = r;
      reject = e;
    });
    readyRef.current = { promise, resolve, reject };
  }

  // Holds the mixpanel instance only AFTER init() has fully run (token + config set). Event methods
  // gate on this — i.e. "init succeeded" — rather than on "the chunk loaded". Calling mixpanel.track()
  // on a loaded-but-uninitialized instance would have no token. Reading a ref also avoids the
  // stale-closure trap of checking `state.enabled` after an await.
  const mixpanelRef = useRef<MixpanelInstance | null>(null);

  const shareTokenValue = shareToken.get();

  const replayTokenValue = replayToken.get();
  const loggedInUserId = shareToken.loggedInUserId ?? replayToken.loggedInUserID;

  const hasSharedReplayProjectToken = shareTokenValue || replayTokenValue;

  const hasNonLoggedInGuestUserWithShareProject = !loggedInUserId && hasSharedReplayProjectToken;

  const getUserDetails = (email?: string, firstName?: string, lastName?: string) => {
    const tab_session_id = getSessionValue();
    let userEmail = currentUserData?.email ?? email ?? 'Public_User_' + tab_session_id;

    let userId = currentUserData?.id;
    let userFirstName = firstName;
    let userLastName = lastName;
    //Case when user has opened the replay or share project and not logged in
    if (hasNonLoggedInGuestUserWithShareProject) {
      userEmail = 'Public_Guest_User_Share_Project_' + tab_session_id;
      userLastName = '';
      userFirstName = userEmail;
      userId = userFirstName;
      //Case when user has opened the replay or share project and logged in with some credentials
    } else if (loggedInUserId && hasSharedReplayProjectToken) {
      userEmail = loggedInUserData?.email ?? '';
      userId = loggedInUserId;
      userFirstName = loggedInUserData?.firstName;
      userLastName = loggedInUserData?.lastName;
    }
    return { userEmail, userId, userFirstName, userLastName };
  };
  const isMixpanelAllowedHost = typeof window !== 'undefined' && MIXPANEL_ALLOWED_HOSTS.includes(window.location.host);

  const initMixpanel = usePersistentCallback(async (email?: string) => {
    const { userEmail } = getUserDetails(email);
    const mixpanel = await loadMixpanel();
    if (!mixpanel) {
      // Chunk failed to load — unblock any events awaiting init so they drop gracefully instead of
      // awaiting a promise that never settles. Analytics is degraded for this session; FullStory/Sentry
      // (which don't depend on mixpanel) still work.
      readyRef.current?.reject(new Error('Failed to load mixpanel-browser'));
      return;
    }
    setState((draft) => {
      draft.isMixPanelLoaded = true;
      draft.enabled = true;
    });
    mixpanel.init(MIXPANEL___TOKEN, {
      api_host: getMixpanelApIhost(),
      ignore_dnt: true,
      // Enable debugging to log mixpanel metrics in dev mode
      debug: false,
      // On allowed hosts: use cookies scoped to FQDN only.
      // On all other hosts (private/dedicated SaaS): use localStorage, no cookies.
      ...(isMixpanelAllowedHost
        ? { cross_subdomain_cookie: false, cookie_domain: window.location.hostname }
        : { persistence: 'localStorage' as const, disable_cookie: true })
    });
    mixpanel.set_config({
      xhr_headers: {
        [csrf.key]: csrf.get(),
        ...(shareTokenValue && { [shareToken.key]: shareTokenValue }),
        ...(replayTokenValue && { [replayToken.key]: replayTokenValue })
      }
    });
    mixpanel.register({
      session_id: getSessionValue(),
      user_type: userEmail,
      window_width: window.innerWidth,
      window_height: window.innerHeight
    });

    mixpanelRef.current = mixpanel;
    // Unblock any event calls that were fired (and are awaiting) before init finished.
    readyRef.current?.resolve();
  });

  const loadFullStory = usePersistentCallback(async () => {
    const enabled =
      localStorage.getItem(FULLSTORY_ENABLE_KEY) === 'true' || FullStoryAllowedDomains.includes(window.location.host);

    FullStoryInit({
      orgId: fullStoryOrgID,
      namespace: fullStoryNameSpace,
      // Enable in dev mode to log full story metrics
      devMode: !enabled
    });
  });

  const addContext = usePersistentCallback(<T extends keyof MixPanelContext>(key: T, value: MixPanelContext[T]) => {
    setState((draft) => {
      draft.contexts[key] = value;
    });
  });

  const removeContext = usePersistentCallback((key: MixPanelContextKeys) => {
    setState((draft) => {
      delete draft.contexts[key];
    });
  });

  const privateMixpanelInit = usePersistentCallback(async () => {
    if (!isMixPanelEnabled) return;
    if (!state.isUserIdentified && !state.isMixPanelLoaded) {
      try {
        await setDistinctID();
      } catch (error) {
        console.error(error);
      }
    }
  });

  const publicInitMixpanel = usePersistentCallback(async () => {
    if (!isMixPanelEnabled) return;
    try {
      if (!state.isMixPanelLoaded) {
        await initMixpanel();
        await track(EntityEvents[Entity.User].home.nonLoggedinUserFirstEvent);
      }
    } catch (error) {
      console.error(error);
    }
  });

  const track = usePersistentCallback(
    async (eventName: string, props: { [key: string]: unknown } = {}, event_category?: string) => {
      try {
        if (!isMixPanelEnabled) return;
        // Wait for the init attempt, then bail if it failed — restoring the original `if (state.enabled)`
        // gate. We check the initialized instance (set only after mixpanel.init succeeded), not merely
        // "the chunk loaded", so we never track against an uninitialized mixpanel and we don't emit the
        // FullStory event / breadcrumb either when init failed.
        await readyRef.current?.promise;

        if (!mixpanelRef.current) {
          throw new Error('Mixpanel not initialized');
        }

        if (event_category) {
          props['event_category'] = event_category;
        }
        const { userId, userEmail } = getUserDetails();
        const tab_session_id = getSessionValue();
        let trackProps = {
          ...props,
          tab_session_id,
          // TODO: this need to be changed with new event data structure, this is temporary to send window size details
          window_width: window.innerWidth,
          window_height: window.innerHeight
        } as { [key: string]: unknown };
        if (userId) {
          trackProps.email = userEmail;
          trackProps.userId = userId;
        }

        mixpanelRef.current.track(eventName, {
          event_data: trackProps,
          contexts: { ...state.contexts }
        });
        //Track event in full story
        fullStoryEvent(eventName, {
          event_data: trackProps,
          contexts: { ...state.contexts }
        });
        addBreadcrumb({
          category: SentryBreadcrumb.Mixpanel,
          message: eventName,
          level: 'info'
        });
      } catch (error) {
        console.log('Failed to track event', eventName, error);
      }
    }
  );

  const setDistinctID = usePersistentCallback(async (currentEmail?: string) => {
    const { userEmail, userId } = getUserDetails(currentEmail);
    if (isMixPanelEnabled) {
      await initMixpanel(userEmail);
      await track(EntityEvents[Entity.User].home.loggedInUserFirstEvent, {
        userId: userId,
        email: userEmail,
        source: currentUserData?.type
      });
      metrics.track({
        eventName: EventName.ui.metadata.ui.page.load,
        eventType: EventType.userAction,
        payload: { userId: userId ?? '', email: userEmail ?? '', source: currentUserData?.type ?? '' }
      });
    }
    const tab_session_id = getSessionValue();
    if (userEmail) {
      const mixpanel = mixpanelRef.current;
      if (mixpanel) {
        mixpanel.identify(userEmail);
        mixpanel.people.set({
          $email: userEmail,
          $userId: userId,
          $last_seen: new Date().toISOString(),
          $tab_session_id: tab_session_id,
          $time: getCurrentTimeStamp(),
          $insert_id: nanoid(10)
        });
      }

      //TODO remove after ff poc
      identify(userEmail, {
        displayName: userEmail,
        email: userEmail,
        tab_session_id,
        useID: userId
      });
      setState((draft) => {
        draft.isUserIdentified = true;
      });
    }
  });

  const setOnceEvent = usePersistentCallback(async (props: { [key: string]: string | undefined }) => {
    if (!isMixPanelEnabled) return;
    await readyRef.current?.promise;
    //Check if user is not identified just before setting event
    if (!state.isUserIdentified) {
      await setDistinctID(props?.email || currentUserData?.email);
    }
    const mixpanel = mixpanelRef.current;
    if (mixpanel) mixpanel.people.set_once(props);
  });

  const identifyAndSetPeople = usePersistentCallback(
    async (
      email: string,
      obj: {
        firstName: string;
        country?: string;
        lastName?: string;
        source?: UserTypes;
        invitation?: string;
        userId?: string;
        company?: string;
        signup_date?: string;
      }
    ) => {
      if (!isMixPanelEnabled) return;
      await readyRef.current?.promise;
      obj = obj || {};
      const firstName = obj.firstName;
      const lastName = obj.lastName && obj.lastName !== '--' ? obj.lastName : '';
      const { userId, userEmail, userFirstName, userLastName } = getUserDetails(email, firstName, lastName);
      //Identify user if not identified or override the user detail again if something name or anything else changed
      await setDistinctID(userEmail);

      const name =
        userFirstName || userLastName
          ? `${userFirstName ? userFirstName : ''} ${userLastName}`
          : getNameFromEmail(userEmail);
      const invitation = obj?.invitation;
      const tab_session_id = getSessionValue();
      const peopleObj: { [key: string]: string | undefined } = {
        $name: name,
        $email: userEmail,
        id: userEmail,
        $company: obj.company,
        $userId: userId,
        $firstName: userFirstName,
        $lastName: userLastName,
        $signup_date: obj.signup_date,
        $source: obj.source || currentUserData?.type,
        $last_seen: new Date().toISOString(),
        $tab_session_id: tab_session_id,
        $time: getCurrentTimeStamp(),
        $insert_id: nanoid(10)
      };

      if (invitation) {
        peopleObj.$invitation = invitation;
      }
      setUserVars(peopleObj);
      const mixpanel = mixpanelRef.current;
      if (mixpanel) mixpanel.people.set(peopleObj);
      setUser({ email, username: name, ...obj, lastName: userLastName, uid: obj.userId });
    }
  );

  return useMemo(
    () => ({
      publicInitMixpanel,
      setOnceEvent,
      track,
      loadFullStory,
      privateMixpanelInit,
      identifyAndSetPeople,
      addContext,
      removeContext
    }),
    [
      privateMixpanelInit,
      publicInitMixpanel,
      loadFullStory,
      track,
      identifyAndSetPeople,
      setOnceEvent,
      addContext,
      removeContext
    ]
  );
};
